import React from 'react'
import { TrendingUp, TrendingDown } from 'lucide-react'

const fmt = (n) =>
  n !== undefined && n !== null
    ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—'

// Per-account equity chip (inside the dark bar)
const AccountChip = ({ label, value, pnl }) => {
  const pos = (pnl || 0) >= 0
  return (
    <div className="flex flex-col items-center bg-white border border-gray-200 rounded-lg px-4 py-2 min-w-[120px]">
      <span className="text-xs font-medium text-gray-500 mb-1">{label}</span>
      <span className="text-sm font-bold text-gray-800">₹{fmt(value)}</span>
      <span className={`text-xs font-medium ${pos ? 'text-success-600' : 'text-danger-600'}`}>
        {pos ? '+' : ''}₹{fmt(pnl)}
      </span>
    </div>
  )
}

/**
 * PortfolioSummaryBar
 *
 * Props:
 *  accounts[]   — equity accounts with { accountName, label, summary }
 *  mfTotal      — MF active current value (₹)
 *  mfSummary    — full MF summary: { totalValue, totalPnl, activeInvested, soldPnl, soldInvested }
 */
const PortfolioSummaryBar = ({ accounts = [], mfTotal = 0, mfSummary = {}, uniqueEquityCount }) => {
  // ── Equity aggregates ────────────────────────────────────────────────────
  const equityValue    = accounts.reduce((s, a) => s + (a.summary?.totalValue || 0), 0)
  const equityPnl      = accounts.reduce((s, a) => s + (a.summary?.totalPnl   || 0), 0)
  const equityInvested = accounts.reduce((s, a) => s + ((a.summary?.totalValue || 0) - (a.summary?.totalPnl || 0)), 0)

  // ── MF breakdown ─────────────────────────────────────────────────────────
  const mfActivePnl      = mfSummary.totalPnl       || 0
  const mfSoldPnl        = mfSummary.soldPnl        || 0
  const mfActiveInvested = mfSummary.activeInvested  || 0
  const mfSoldInvested   = mfSummary.soldInvested    || 0   // cost basis of redeemed MF units

  // ── Overall ───────────────────────────────────────────────────────────────
  const overallValue   = equityValue + mfTotal
  // Overall P&L = equity unrealised + MF unrealised + MF realised (sold)
  const overallPnl     = equityPnl + mfActivePnl + mfSoldPnl
  const overallPnlPos  = overallPnl >= 0

  // "Total Invested" displayed to the user = what is CURRENTLY deployed:
  //   equity cost basis + active MF cost basis (no redeemed/sold MF).
  // Redeemed MF cost (soldInvested) is excluded from the label because those
  // funds are no longer invested — showing them inflated the number (e.g.
  // ₹87L instead of the actual ₹58L currently at risk).
  const totalInvested  = equityInvested + mfActiveInvested

  // For the P&L% we include soldInvested in the denominator so the rate is
  // conservative/honest (realized losses reduce the overall %). The note
  // "₹X MF realised" under the invested card makes this visible.
  const pnlDenominator = totalInvested + mfSoldInvested
  const overallPnlPct  = pnlDenominator > 0 ? (overallPnl / pnlDenominator) * 100 : 0

  return (
    <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-5 text-white mb-6">

      {/* ── Header: Overall (left) | [Total Invested + P&L] (right) ── */}
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">

        {/* Left: Overall portfolio value */}
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Overall Portfolio</p>
          <p className="text-3xl font-bold">₹{fmt(overallValue)}</p>
        </div>

        {/* Right: Total Invested + P&L grouped side-by-side.
            portrait:flex-wrap  — wraps P&L to next line on portrait screens when
                                  numbers are too wide (fixes mobile vertical overflow).
            portrait:justify-end — right-aligns the wrapped P&L badge on that line.
            No effect on landscape / desktop / mobile horizontal. */}
        <div className="flex items-center gap-3 portrait:flex-wrap portrait:justify-end">

          {/* Total Invested card */}
          <div className="bg-white/10 rounded-xl px-5 py-3 text-center border border-white/10">
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Total Invested</p>
            <p className="text-xl font-bold">₹{fmt(totalInvested)}</p>
            {mfSoldPnl !== 0 && (
              <p className={`text-xs mt-0.5 ${mfSoldPnl >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                {mfSoldPnl >= 0 ? '+' : ''}₹{fmt(mfSoldPnl)} MF realised
              </p>
            )}
          </div>

          {/* Overall P&L badge */}
          <div className={`flex items-center gap-2 px-4 py-3 rounded-xl ${overallPnlPos ? 'bg-success-900/40' : 'bg-danger-900/40'}`}>
            {overallPnlPos
              ? <TrendingUp className="w-5 h-5 text-success-400" />
              : <TrendingDown className="w-5 h-5 text-danger-400" />}
            <div>
              <p className={`text-xs uppercase tracking-wide mb-0.5 ${overallPnlPos ? 'text-success-500' : 'text-danger-500'}`}>
                Total P&amp;L
              </p>
              <p className={`text-lg font-bold ${overallPnlPos ? 'text-success-400' : 'text-danger-400'}`}>
                {overallPnlPos ? '+' : ''}₹{fmt(overallPnl)}
              </p>
              <p className={`text-xs ${overallPnlPos ? 'text-success-500' : 'text-danger-500'}`}>
                {overallPnlPos ? '+' : ''}{fmt(overallPnlPct)}%
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* ── Per-account chips ── */}
      <div className="flex gap-3 overflow-x-auto pb-1 mb-4">
        {accounts.map((a) => (
          <AccountChip
            key={a.accountName}
            label={a.label}
            value={a.summary?.totalValue}
            pnl={a.summary?.totalPnl}
          />
        ))}

        {/* Mutual Funds chip — now shows real data */}
        <div className="flex flex-col items-center bg-white/10 border border-white/20 rounded-lg px-4 py-2 min-w-[120px]">
          <span className="text-xs font-medium text-gray-400 mb-1">Mutual Funds</span>
          <span className="text-sm font-bold text-gray-200">₹{fmt(mfTotal)}</span>
          {mfActivePnl !== 0 ? (
            <span className={`text-xs font-medium ${mfActivePnl >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
              {mfActivePnl >= 0 ? '+' : ''}₹{fmt(mfActivePnl)}
            </span>
          ) : (
            <span className="text-xs text-gray-500">—</span>
          )}
        </div>
      </div>

      {/* ── Bottom breakdown row ── */}
      <div className="flex gap-4 border-t border-white/10 pt-3 flex-wrap">
        <div className="flex-1 min-w-[80px]">
          <p className="text-xs text-gray-400">Equity Value</p>
          <p className="text-sm font-semibold">₹{fmt(equityValue)}</p>
        </div>
        <div className="flex-1 min-w-[80px]">
          <p className="text-xs text-gray-400">Equity P&amp;L</p>
          <p className={`text-sm font-semibold ${equityPnl >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
            {equityPnl >= 0 ? '+' : ''}₹{fmt(equityPnl)}
          </p>
        </div>
        <div className="flex-1 min-w-[80px]">
          <p className="text-xs text-gray-400">MF Active P&amp;L</p>
          <p className={`text-sm font-semibold ${mfActivePnl >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
            {mfActivePnl !== 0 ? `${mfActivePnl >= 0 ? '+' : ''}₹${fmt(mfActivePnl)}` : '—'}
          </p>
        </div>
        <div className="flex-1 min-w-[80px]">
          <p className="text-xs text-gray-400">MF Realised P&amp;L</p>
          <p className={`text-sm font-semibold ${mfSoldPnl >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
            {mfSoldPnl !== 0 ? `${mfSoldPnl >= 0 ? '+' : ''}₹${fmt(mfSoldPnl)}` : '—'}
          </p>
        </div>
        <div className="flex-1 min-w-[80px]">
          <p className="text-xs text-gray-400">Total Holdings</p>
          <p className="text-sm font-semibold">
            {uniqueEquityCount ?? accounts.reduce((s, a) => s + (a.summary?.holdingsCount || 0), 0)} stocks
          </p>
        </div>
      </div>
    </div>
  )
}

export default PortfolioSummaryBar
